/**
 * Time simulation engine for educational playback of market scenarios.
 * Supports variable speed control: paused, slow (0.25x), normal (1x), fast (4x).
 */

// =============================================================================
// Types
// =============================================================================

/**
 * Available simulation speeds.
 */
export type SimulationSpeed = 'paused' | 'slow' | 'normal' | 'fast'

/**
 * State of the time simulation.
 */
export interface TimeSimulation {
  /** Current playback speed */
  speed: SimulationSpeed
  /** Simulated timestamp in milliseconds */
  currentTime: number
  /** Real timestamp when simulation started */
  realTimeStart: number
  /** Real timestamp when paused, or null if running */
  pausedAt: number | null
}

/**
 * Speed multipliers for each simulation speed.
 * - paused: 0 (time frozen)
 * - slow: 0.25x (4 seconds real = 1 second simulated)
 * - normal: 1x (real-time)
 * - fast: 4x (1 second real = 4 seconds simulated)
 */
export const SPEED_MULTIPLIERS = {
  paused: 0,
  slow: 0.25,
  normal: 1,
  fast: 4,
} as const

// =============================================================================
// TimeSimulationEngine
// =============================================================================

/**
 * Engine for controlling time in market simulations.
 * Useful for educational scenarios where users can slow down or speed up
 * market activity to better understand order matching and price discovery.
 */
export class TimeSimulationEngine {
  private simulation: TimeSimulation
  private intervalId: ReturnType<typeof setInterval> | null = null
  private onTick: (time: number) => void

  /**
   * Create a new time simulation engine.
   * @param onTick - Callback invoked every 100ms real-time with the current simulated time
   */
  constructor(onTick: (time: number) => void) {
    this.onTick = onTick
    this.simulation = {
      speed: 'paused',
      currentTime: 0,
      realTimeStart: Date.now(),
      pausedAt: Date.now(),
    }
  }

  /**
   * Set the simulation speed.
   * Setting to 'paused' will pause the simulation.
   * Setting to any other speed will resume/continue playback.
   *
   * @param speed - The desired simulation speed
   */
  setSpeed(speed: SimulationSpeed): void {
    if (speed === 'paused') {
      this.pause()
    } else {
      this.resume(speed)
    }
  }

  /**
   * Get the current simulation speed.
   */
  getSpeed(): SimulationSpeed {
    return this.simulation.speed
  }

  /**
   * Pause the simulation and record the pause timestamp.
   */
  private pause(): void {
    this.simulation.speed = 'paused'
    this.simulation.pausedAt = Date.now()
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  /**
   * Resume the simulation at the specified speed.
   * Adjusts realTimeStart to account for time spent paused.
   *
   * @param speed - The speed to resume at (cannot be 'paused')
   */
  private resume(speed: Exclude<SimulationSpeed, 'paused'>): void {
    if (this.simulation.pausedAt !== null) {
      // Adjust for paused duration so currentTime continues from where it was
      this.simulation.realTimeStart += Date.now() - this.simulation.pausedAt
      this.simulation.pausedAt = null
    }
    this.simulation.speed = speed

    // Clear any existing interval
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
    }

    // Tick every 100ms real time
    this.intervalId = setInterval(() => {
      const elapsed = Date.now() - this.simulation.realTimeStart
      this.simulation.currentTime = elapsed * SPEED_MULTIPLIERS[this.simulation.speed]
      this.onTick(this.simulation.currentTime)
    }, 100)
  }

  /**
   * Reset the simulation to its initial state.
   * Pauses playback and resets time to 0.
   */
  reset(): void {
    this.pause()
    this.simulation.currentTime = 0
    this.simulation.realTimeStart = Date.now()
    this.simulation.pausedAt = Date.now()
  }

  /**
   * Get the current simulated time.
   * @returns Current simulated timestamp in milliseconds
   */
  getCurrentTime(): number {
    return this.simulation.currentTime
  }

  /**
   * Get the full simulation state (for debugging/persistence).
   */
  getState(): Readonly<TimeSimulation> {
    return { ...this.simulation }
  }

  /**
   * Clean up the interval when the engine is no longer needed.
   * Call this in component cleanup/unmount.
   */
  destroy(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }
}
